import React, { useMemo } from "react";
import BadgeListControl from "./BadgeListControl";

interface CaseNumberListControlProps {
  label: string;
  values: string[];
  isTrialworks: boolean;
  isSourceApi: boolean;
  onChange: (values: string[]) => void;
  disabled?: boolean;
  helpText?: string;
}

const TW_FULL = /^tw-(\d+-?)+\d+$/i;
const NUMERIC = /^(\d+-)?\d+$/;
// Folder names may contain spaces, so we only split on comma, semicolon,
// tab, or newline — not the default whitespace class.
const FOLDER_NAME_SEPARATOR = /[,;\t\n\r]+/;

const CaseNumberListControl: React.FC<CaseNumberListControlProps> = ({
  label,
  values,
  isTrialworks,
  isSourceApi,
  onChange,
  disabled,
  helpText,
}) => {
  const validateEntry = useMemo(() => {
    // Filevine / Dropbox use opaque external identifiers — accept anything non-empty.
    if (isSourceApi) {
      return (input: string) => (input ? input : null);
    }
    // TrialWorks: accept TW-prefixed or bare numeric (auto-prefix bare to TW-).
    if (isTrialworks) {
      return (input: string) => {
        if (TW_FULL.test(input)) return input;
        if (NUMERIC.test(input)) return `TW-${input}`;
        return null;
      };
    }
    // DocsToNeos: cases are folder names — accept any non-empty token.
    return (input: string) => (input ? input : null);
  }, [isSourceApi, isTrialworks]);

  return (
    <BadgeListControl
      label={label}
      values={values}
      onChange={onChange}
      disabled={disabled}
      validateEntry={validateEntry}
      addPlaceholder={
        isSourceApi
          ? "Type case identifiers, separated by comma or space…"
          : isTrialworks
            ? "Type case numbers, separated by comma or space…"
            : "Type case folder names, separated by comma, semicolon, or newline…"
      }
      tooltipText={
        !isSourceApi && !isTrialworks
          ? "Accepts folder names typed individually or separated by comma, semicolon, tab, or newline. Spaces inside a folder name are preserved."
          : undefined
      }
      separator={
        !isSourceApi && !isTrialworks ? FOLDER_NAME_SEPARATOR : undefined
      }
      helpText={helpText}
      entityLabel={isTrialworks || isSourceApi ? "case numbers" : "case folder names"}
    />
  );
};

export default React.memo(CaseNumberListControl);
